import { useEffect, useId, useRef, type ReactNode } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Render above other open modals (used by stacked confirmations). */
  elevated?: boolean;
};

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

// Tokens of every currently-open Modal, in open order. Only the topmost one
// reacts to Escape/Tab, so a confirmation stacked over another modal dismisses
// just itself rather than both.
const modalStack: string[] = [];

export function Modal({ open, onClose, title, children, footer, size = 'md', elevated = false }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Keep the latest onClose in a ref so the focus-management effect below can
  // call it without listing it as a dependency. Parents commonly pass an inline
  // `onClose={() => ...}`, which is a new function every render; if that effect
  // depended on it, every keystroke would re-run it and yank focus back to the
  // first field (the original "name input keeps refocusing email" bug).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    // Remember what had focus so we can restore it when the dialog closes.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    modalStack.push(titleId);

    function focusable(): HTMLElement[] {
      const root = panelRef.current;
      if (!root) return [];
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    // Move focus into the dialog: first focusable element, or the panel itself.
    (focusable()[0] ?? panelRef.current)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      // Only the topmost open modal handles keys.
      if (modalStack[modalStack.length - 1] !== titleId) return;
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap Tab focus within the dialog so it can't wander to the page behind.
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = modalStack.indexOf(titleId);
      if (i !== -1) modalStack.splice(i, 1);
      previouslyFocused?.focus?.();
    };
  }, [open, titleId]);

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 ${elevated ? 'z-[60]' : 'z-50'} flex items-center justify-center bg-slate-900/40 p-4`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`w-full ${SIZES[size]} rounded-xl bg-white shadow-xl focus:outline-none`}
      >
        <div className="border-b border-slate-100 px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer !== undefined && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
